(() => {
  const cleanups = new WeakMap();
  const init = root => {
    if (root.dataset.homeInitialized) return;
    root.dataset.homeInitialized = 'true';
    const lifetime = new AbortController();
    const disposers = [];
    const cleanup = () => { lifetime.abort(); disposers.forEach(dispose => dispose()); };
    cleanups.set(root, cleanup);
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const deal = root.querySelector('#daily');
    const teaser = root.querySelector('[data-home-intermission]');
    const noDeal = root.querySelector('[data-home-no-deal]');
    root.querySelectorAll('[data-featured-collection]').forEach(section => {
      const grid = section.querySelector('.home-after-hours-grid');
      if (!grid) return;
      const templates = [...grid.querySelectorAll('template[data-featured-card]')];
      const cards = [...grid.querySelectorAll(':scope > .product-card'), ...templates.map(template => template.content.querySelector('.product-card'))];
      if (cards.length > 5) {
        const storageKey = `cheapundies:home:${section.dataset.featuredCollection}:last-five:v1`;
        let previous = [];
        try {
          const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
          if (Array.isArray(saved)) previous = saved;
        } catch (_) {
          // A private session can block storage; selection still works.
        }
        const shuffle = items => {
          for (let i = items.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [items[i], items[j]] = [items[j], items[i]];
          }
          return items;
        };
        const fresh = shuffle(cards.filter(card => !previous.includes(card.dataset.productHandle)));
        const selected = fresh.slice(0, 5);
        if (selected.length < 5) selected.push(...shuffle(cards.filter(card => !selected.includes(card))).slice(0, 5 - selected.length));
        grid.replaceChildren(...selected);
        selected.forEach(card => card.classList.add('is-picked'));
        grid.dataset.randomized = 'true';
        grid.dispatchEvent(new Event('shopify:section:load', { bubbles: true }));
        try {
          localStorage.setItem(storageKey, JSON.stringify(selected.map(card => card.dataset.productHandle)));
        } catch (_) {
          // Storage is optional for avoiding consecutive repeats.
        }
      }
    });
    const scheduledStart = Date.parse(root.dataset.nextDealStarts || '');
    const launchLabel = root.querySelector('[data-next-launch-label]');
    if (launchLabel && Number.isFinite(scheduledStart)) {
      launchLabel.textContent = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
      }).format(scheduledStart);
    }
    window.CheapUndiesDeal?.sync(root.dataset.serverNow);
    const scheduleNow = () => window.CheapUndiesDeal?.now() ?? Date.now();
    let refreshPending = false;
    let previewRefreshRequested = false;
    let nextRefreshAttempt = Date.now() + 30000;
    const showDealState = mode => {
      if (!deal || !noDeal) return;
      if (root.dataset.homeMode === 'live' && mode === 'none') nextRefreshAttempt = 0;
      root.dataset.homeMode = mode;
      noDeal.hidden = mode !== 'none';
      if (teaser) teaser.hidden = mode !== 'teaser';
      deal.querySelectorAll('[data-home-live]').forEach(element => { element.hidden = mode !== 'live'; });
      deal.setAttribute('aria-labelledby', mode === 'teaser' ? 'intermission-title' : mode === 'live' ? 'deal-title' : 'no-deal-title');
    };
    const refreshDealState = async requireLaunch => {
      if (refreshPending || (root.contains(document.activeElement) && document.activeElement.closest('form'))) return;
      const wrapper = root.closest('[id^="shopify-section-"]');
      if (!wrapper) return;
      refreshPending = true;
      nextRefreshAttempt = Date.now() + (requireLaunch ? 10000 : 30000);
      try {
        const sectionId = wrapper.id.replace(/^shopify-section-/, '');
        const base = window.Shopify?.routes?.root || '/';
        const url = new URL(base, location.origin);
        url.searchParams.set('section_id', sectionId);
        url.searchParams.set('_', String(Date.now()));
        const response = await fetch(url, { signal: lifetime.signal, cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('The next deal is not available yet.');
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const freshWrapper = documentFragment.getElementById(wrapper.id);
        const freshRoot = freshWrapper?.querySelector('[data-home-reference]');
        const freshDeal = freshRoot?.querySelector('#daily');
        if (!freshRoot || !freshDeal) return;
        window.CheapUndiesDeal?.sync(freshRoot.dataset.serverNow);
        if (requireLaunch) {
          // The live pointer may advance past the preview (including another window today).
          // Accept the server's active window, never a client-side calendar prediction.
          const confirmed = freshRoot.dataset.homeMode === 'live' && Boolean(freshDeal.dataset.dealCollectionId);
          const scheduleChanged = freshRoot.dataset.nextDealStarts && freshRoot.dataset.nextDealStarts !== root.dataset.nextDealStarts;
          const scheduleRemoved = freshRoot.dataset.homeMode === 'none' && !freshRoot.dataset.nextDealStarts;
          if (!confirmed && !scheduleChanged && !scheduleRemoved) return;
        }
        if (!root.isConnected || lifetime.signal.aborted || (root.contains(document.activeElement) && document.activeElement.closest('form'))) return;
        if (!requireLaunch && freshRoot.dataset.dealRevision === root.dataset.dealRevision) return;
        const restoreFocus = teaser?.contains(document.activeElement);
        wrapper.dispatchEvent(new Event('shopify:section:unload', { bubbles: true }));
        cleanup();
        wrapper.replaceWith(freshWrapper);
        freshWrapper.dispatchEvent(new Event('shopify:section:load', { bubbles: true }));
        if (restoreFocus) {
          const heading = freshRoot.querySelector('#deal-title');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus();
        }
      } catch (_) {
        // The updater may still be publishing the scheduled collection. Retry safely.
      } finally {
        refreshPending = false;
      }
    };
    if (deal) {
      const tickDealState = () => {
        if (!root.isConnected) { cleanup(); return; }
        root.querySelectorAll('[data-home-return-until]').forEach(claim => {
          if (scheduleNow() >= Date.parse(claim.dataset.homeReturnUntil) || deal.classList.contains('is-ended')) claim.remove();
        });
        const remaining = scheduledStart - scheduleNow();
        if (teaser && remaining <= 900000 && root.dataset.teaserWindow !== 'true' && !previewRefreshRequested) {
          previewRefreshRequested = true;
          nextRefreshAttempt = 0;
        }
        if (teaser && Number.isFinite(scheduledStart) && root.dataset.homeMode !== 'live') {
          showDealState('teaser');
          const clock = teaser.querySelector('.home-intermission-countdown');
          const ready = teaser.querySelector('[data-home-intermission-ready]');
          if (remaining > 0) {
            clock.hidden = false;
            ready.hidden = true;
            const seconds = Math.max(0, Math.ceil(remaining / 1000));
            [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60].forEach((part, index) => {
              teaser.querySelector(`[data-next-time-part="${index}"]`).textContent = String(part).padStart(2, '0');
            });
          } else {
            clock.hidden = true;
            ready.hidden = false;
          }
        } else if (deal.classList.contains('is-ended')) {
          showDealState('none');
        }
        if (Date.now() >= nextRefreshAttempt && !document.hidden) refreshDealState(Boolean(teaser && Number.isFinite(scheduledStart) && remaining <= 0));
      };
      const dealStateTimer = setInterval(tickDealState, 1000);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) tickDealState(); }, { signal: lifetime.signal });
      disposers.push(() => clearInterval(dealStateTimer));
      tickDealState();
    }
    // The shared theme script owns deadline/expiry state. This only formats its output.
    const countdown = root.querySelector('[data-countdown]');
    const clock = root.querySelector('.home-clock');
    if (countdown && clock) {
      const paintClock = () => {
        const parts = countdown.textContent.trim().match(/^(\d+)\s*:\s*(\d+)\s*:\s*(\d+)$/);
        clock.hidden = !parts;
        countdown.classList.toggle('visually-hidden', Boolean(parts));
        if (parts) clock.querySelectorAll('[data-time-part]').forEach((node, i) => { node.textContent = parts[i + 1]; });
      };
      const observer = new MutationObserver(paintClock);
      observer.observe(countdown, { childList: true, characterData: true, subtree: true });
      disposers.push(() => observer.disconnect());
      paintClock();
    }
    const carousel = root.querySelector('[data-home-carousel]');
    if (carousel) {
      const track = carousel.querySelector('[data-carousel-track]');
      const toggle = carousel.querySelector('[data-carousel-toggle]');
      const controls = carousel.querySelector('[data-carousel-controls]');
      let stopped = reducedMotion.matches, hovered = false, focused = false, timer;
      const isScrollable = () => track.scrollWidth > track.clientWidth + 2;
      const update = () => {
        controls.hidden = !isScrollable();
        toggle.textContent = stopped ? 'Play' : 'Pause';
        toggle.setAttribute('aria-label', `${stopped ? 'Start' : 'Pause'} automatic product scrolling`);
      };
      const advance = direction => {
        const max = track.scrollWidth - track.clientWidth;
        const step = (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth) + 13;
        const atEnd = track.scrollLeft >= max - 2;
        const atStart = track.scrollLeft <= 2;
        const next = direction > 0 && atEnd ? 0 : direction < 0 && atStart ? max : track.scrollLeft + direction * step;
        track.scrollTo({ left: Math.max(0, Math.min(max, next)), behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      };
      const schedule = () => {
        clearInterval(timer);
        if (!stopped && !hovered && !focused && !document.hidden && isScrollable() && root.isConnected) timer = setInterval(() => { if (!root.isConnected) { clearInterval(timer); return; } advance(1); }, 5000);
        update();
      };
      toggle.addEventListener('click', () => { stopped = !stopped; schedule(); });
      carousel.querySelector('[data-carousel-prev]').addEventListener('click', () => { stopped = true; advance(-1); schedule(); });
      carousel.querySelector('[data-carousel-next]').addEventListener('click', () => { stopped = true; advance(1); schedule(); });
      carousel.addEventListener('mouseenter', () => { hovered = true; schedule(); });
      carousel.addEventListener('mouseleave', () => { hovered = false; schedule(); });
      carousel.addEventListener('focusin', event => { focused = true; if (event.target !== toggle) stopped = true; schedule(); });
      carousel.addEventListener('focusout', event => { if (!carousel.contains(event.relatedTarget)) { focused = false; schedule(); } });
      track.addEventListener('pointerdown', () => { stopped = true; schedule(); });
      track.addEventListener('keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); stopped = true; advance(event.key === 'ArrowRight' ? 1 : -1); schedule();
      });
      reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) stopped = true; schedule(); }, { signal: lifetime.signal });
      document.addEventListener('visibilitychange', schedule, { signal: lifetime.signal });
      disposers.push(() => clearInterval(timer));
      const observer = new ResizeObserver(schedule); observer.observe(track);
      disposers.push(() => observer.disconnect());
      const updateDealCards = () => {
        const soldOut = [...track.querySelectorAll(':scope > .product-card.sold-out')];
        soldOut.forEach(card => card.remove());
        if (soldOut.length) nextRefreshAttempt = 0;
        const cards = [...track.querySelectorAll(':scope > .product-card')];
        cards.forEach((card, index) => { const title = card.querySelector('h3')?.textContent.trim() || ''; card.setAttribute('aria-label', `${index + 1} of ${cards.length}: ${title}`); });
        carousel.hidden = !cards.length || root.dataset.homeMode !== 'live';
        const empty = root.querySelector('[data-home-deal-empty]');
        if (empty) empty.hidden = cards.length > 0 || root.dataset.homeMode !== 'live';
        schedule();
      };
      const stockObserver = new MutationObserver(updateDealCards);
      stockObserver.observe(track, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      disposers.push(() => stockObserver.disconnect());
      updateDealCards();
    }
    const categories = root.querySelector('[data-category-track]');
    const categoryControls = root.querySelector('[data-category-controls]');
    if (categories && categoryControls) {
      const previous = categoryControls.querySelector('[data-category-prev]');
      const next = categoryControls.querySelector('[data-category-next]');
      const update = () => {
        const max = categories.scrollWidth - categories.clientWidth;
        categoryControls.hidden = max <= 2;
        previous.disabled = categories.scrollLeft <= 2;
        next.disabled = categories.scrollLeft >= max - 2;
      };
      const advance = direction => categories.scrollBy({ left: direction * categories.clientWidth * .75, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      previous.addEventListener('click', () => advance(-1));
      next.addEventListener('click', () => advance(1));
      categories.addEventListener('scroll', update, { passive: true });
      const observer = new ResizeObserver(update); observer.observe(categories);
      disposers.push(() => observer.disconnect());
      update();
    }
  };
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-home-reference]').forEach(root => cleanups.get(root)?.()));
  document.querySelectorAll('[data-home-reference]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-home-reference]').forEach(init));
})();
